import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Routes from '../../../../constants/routes';
import LoadingIndicator from '../../../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../../../Common/Notification';
import Icon from '../../../Common/Icon';
import { TASK_LIST_VIEWS as VIEWS, TASK_LIST_MENU_ITEMS } from '../../../../constants/constants';
import { CUSTOMER_ROLES } from '../../../../constants/roles';
import '../TaskListPage.scss';
import LocationSelectorDrawer from '../../../Common/LocationSelector/LocationSelectorDrawer';
import MultiSelectBox from '../../../Common/MultiSelectBox/MultiSelectBox';
import { NavigateToRouteWithDashboardParams } from '../../../../helpers/routing';
import { isAllLocationsMode } from '../../../../helpers/taskListHelpers';
import TaskListOption from '../../TaskListOption';
import { actions as locationTaskActions } from '../../../../actions/locationTaskActions';
import {
    useLoadInitialLocationSelectorFilters,
    useLoadLocationSelectorFilters,
} from '../../../../apis/locationTasks';
import {
    mergeLocationKeys,
    shouldApplyDefaultLocation,
    buildLocationsDisplayValues,
    getSelectedLocationsLabel,
    type LocationDisplayValue,
} from './TaskListHeader.logic';

import './TaskListHeader.scss';

const MANAGE_CUSTOM_VIEWS_TEXT = 'Manage Custom Views';
const MANAGE_CUSTOM_VIEWS_ID = -91;
const SAVE_CURRENT_VIEW_TEXT = 'Save Current View';
const SAVE_CURRENT_VIEW_ID = -92;

const viewOptions = [
    { id: VIEWS.List.id, name: VIEWS.List.label, icon: VIEWS.List.icon, value: VIEWS.List.value },
    { id: VIEWS.Calendar.id, name: VIEWS.Calendar.label, icon: VIEWS.Calendar.icon, value: VIEWS.Calendar.value },
    { id: VIEWS.Gantt.id, name: VIEWS.Gantt.label, icon: VIEWS.Gantt.icon, value: VIEWS.Gantt.value },
    {
        id: VIEWS.ResourceAllocation.id,
        name: VIEWS.ResourceAllocation.label,
        icon: VIEWS.ResourceAllocation.icon,
        value: VIEWS.ResourceAllocation.value,
    },
];

interface TaskMenuItem {
    id: number;
    item: { filterName: string; isCustom?: boolean };
}

interface TaskListHeaderProps {
    isMobile: boolean;
    subscriptionId: string;
    taskListState: any;
    urlParams?: Record<string, unknown>;
    selectedView: string;
    onViewChange: (view: string) => void;
    onLocationChange: (locationKeys: string[], skipSearch: boolean) => void;
    onColumnsClick: () => void;
    onFiltersClick: () => void;
    bulkUpdateMode: boolean;
    changeBulkUpdateMode: () => void;
    bulkUpdateResponsibleUser: () => void;
    bulkUpdateStatus: () => void;
    bulkUpdateInactive: () => void;
    bulkUpdateByJobTitle: () => void;
    hasNoFilters: boolean;
    onClearFilters: () => void;
    userRole: string;
    onAddNewTask: () => void;
    onAddNewTaskLegacy?: () => void;
    onSaveViewClick: () => void;
    onCustomViewClick: (filterName: string) => void;
    history: { push: (path: string) => void };
    isSaveAndManageTaskFiltersActive: boolean;
}

export function TaskListHeader(props: TaskListHeaderProps) {
    const dispatch = useDispatch();

    const [showLocationSelectorDrawer, setShowLocationSelectorDrawer] = useState(false);
    const [shouldLoadAllLocations, setShouldLoadAllLocations] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const redux = props.taskListState;
    const reduxLocationKeys: string[] = redux?.searchParams?.locationKeys ?? [];
    const urlLocationKeys: string[] = Array.isArray(props.urlParams?.locationKeys)
        ? (props.urlParams!.locationKeys as string[])
        : [];
    // "All locations" mode selects no location keys at all, so it must not fall back
    // to the default location, and it needs its own display label.
    const isAllLocations = isAllLocationsMode(redux?.searchParams) || isAllLocationsMode(props.urlParams);

    // ── Initial location selector load (once, with the keys known at mount) ──
    // Include URL location parameters to ensure they are loaded even if not yet in Redux state
    const [initialRequest] = useState(() => ({
        subscriptionId: Number(props.subscriptionId),
        locationKeys: mergeLocationKeys(reduxLocationKeys, urlLocationKeys),
    }));

    const { data: initialData, isError: isInitialLocationsError } = useLoadInitialLocationSelectorFilters(initialRequest);

    const hasAppliedInitialData = useRef(false);
    useEffect(() => {
        if (!initialData || hasAppliedInitialData.current) return;
        hasAppliedInitialData.current = true;

        const locations = buildLocationsDisplayValues(initialData.locations);
        const defaultKey = initialData.defaultLocationKey;
        dispatch(locationTaskActions.setLocationSelectorFilters(locations, defaultKey) as any);

        // Apply default location if no locations are selected (URL or Redux)
        if (defaultKey && shouldApplyDefaultLocation(reduxLocationKeys, urlLocationKeys, defaultKey, isAllLocations)) {
            props.onLocationChange([defaultKey], true);
        }
    }, [initialData]);

    // ── Full location list, loaded lazily when the selector drawer opens ─────
    const {
        data: allLocationsData,
        isFetching: isLoadingAllLocations,
        isError: isAllLocationsError,
    } = useLoadLocationSelectorFilters(
        Number(props.subscriptionId),
        { enabled: shouldLoadAllLocations }
    );

    useEffect(() => {
        if (!allLocationsData) return;
        const locations = buildLocationsDisplayValues(allLocationsData.locations);
        dispatch(locationTaskActions.setLocationSelectorFilters(locations, allLocationsData.defaultLocationKey) as any);
    }, [allLocationsData]);

    // A failed selector load leaves the drawer empty, so say so rather than failing silently
    useEffect(() => {
        if (isInitialLocationsError || isAllLocationsError) {
            Notification('error', 'Error', 'Locations could not be loaded');
        }
    }, [isInitialLocationsError, isAllLocationsError]);

    // ── Task views menu ───────────────────────────────────────────────────────
    const taskMenuItems: TaskMenuItem[] = useMemo(
        () => (redux?.tasksPageTasksMenuItems ?? []).map((mi: TaskMenuItem['item'], idx: number) => ({ id: idx, item: mi })),
        [redux?.tasksPageTasksMenuItems]
    );

    const getTasksMenuOptions = () => {
        const { isSaveAndManageTaskFiltersActive } = props;

        const mapTaskMenuItems = () =>
            taskMenuItems.map((mi) => ({
                icon: 'list-check',
                id: mi.id,
                name: mi.item.filterName,
            }));

        const addCustomViewOptions = (isActive: boolean) => [
            {
                icon: 'list-check',
                itemIcon: isActive ? 'pen' : 'lock',
                id: MANAGE_CUSTOM_VIEWS_ID,
                name: MANAGE_CUSTOM_VIEWS_TEXT,
                isSeparator: true,
            },
            {
                icon: 'list-check',
                itemIcon: isActive ? 'floppy-disk' : 'lock',
                id: SAVE_CURRENT_VIEW_ID,
                name: SAVE_CURRENT_VIEW_TEXT,
                isSeparator: false,
            },
        ];

        return [...mapTaskMenuItems(), ...addCustomViewOptions(isSaveAndManageTaskFiltersActive)];
    };

    const onTasksMenuChange = async (menuItemValue: number) => {
        const { onSaveViewClick, onCustomViewClick, taskListState, isSaveAndManageTaskFiltersActive } = props;

        const filter = taskMenuItems.find((x) => x.id === menuItemValue);

        if (menuItemValue === SAVE_CURRENT_VIEW_ID) {
            if (!isSaveAndManageTaskFiltersActive) return;

            onSaveViewClick(); // Prompt user with dialog to save current filters
        } else if (menuItemValue === MANAGE_CUSTOM_VIEWS_ID) {
            if (!isSaveAndManageTaskFiltersActive) return;

            props.history.push(Routes.ManageTaskViews); // Take user to manage views page
        } else if (filter) {
            dispatch(locationTaskActions.setTasksMenuSelected(menuItemValue) as any);

            if (filter.item.isCustom) {
                await onCustomViewClick(filter.item.filterName);
            } else {
                const menuItem = taskListState.tasksMenuItems.find(
                    (x: any) => x.displayName.toLowerCase() === filter.item.filterName.toLowerCase()
                );

                // Special handling for "Tasks" menu item - navigate internally and set default parameters
                if (menuItem.displayName.toLowerCase() === TASK_LIST_MENU_ITEMS.Tasks.value.toLowerCase()) {
                    // Navigate to Tasks route without URL parameters to show default state
                    props.history.push(Routes.Tasks);
                } else {
                    NavigateToRouteWithDashboardParams(Routes.Tasks, menuItem.urlParams, false);
                }
            }
        }
    };

    // ── Location selector drawer ─────────────────────────────────────────────
    const onOpenLocationSelector = useCallback(() => {
        setShowLocationSelectorDrawer(true);

        const locationKeys = redux?.searchParams?.locationKeys ?? [];
        const locations = redux?.locations ?? [];
        /**
         * Only load all locations if the locations list only contains the
         * currently-selected values.  If we've already loaded the locations
         * list with ALL accessible locations (e.g. more values than
         * currently selected) then we don't need to call this again, just
         * because they've chosen to open the location selector drawer again.
         * This saves an operation and hopefully helps performance on
         * subscriptions with many locations.
         */
        if (locationKeys.length >= locations.length) {
            setShouldLoadAllLocations(true);
        }
    }, [redux?.searchParams?.locationKeys, redux?.locations]);

    const onLocationSelectionComplete = useCallback((selectedLocations: LocationDisplayValue[]) => {
        const locationKeys = selectedLocations.map((l) => l.id);
        setShowLocationSelectorDrawer(false);
        dispatch(locationTaskActions.setLocationSelectorFilters(selectedLocations, selectedLocations[0]?.id) as any);
        props.onLocationChange(locationKeys, false);
    }, [dispatch, props.onLocationChange]);

    const onLocationSelectionCancel = useCallback(() => setShowLocationSelectorDrawer(false), []);

    const getSelectedLocationsDisplayNames = () =>
        getSelectedLocationsLabel(redux?.searchParams?.locationKeys, redux?.locations, isAllLocations);

    // ── View tabs ─────────────────────────────────────────────────────────────
    const handleTabClick = (id: number) => {
        setSelectedIndex(id);
        const filteredView = viewOptions.find((view) => view.id === id);
        if (filteredView) props.onViewChange(filteredView.value);
    };

    // ── Render ────────────────────────────────────────────────────────────────

    // Disable the Add Task button, unless there is only a single location selected
    // Also hide the option to bulk update by job title unless there is only a single location selected
    const isMultipleLocationsDisabled = (redux?.searchParams?.locationKeys?.length ?? 0) > 1;

    const toCamelCase = (str: unknown) =>
        typeof str === 'string' && str.trim()
            ? str
                  .replace(/\s(.)/g, (s) => s[1].toUpperCase())
                  .replace(/\s/g, '')
                  .replace(/^(.)/, (s) => s.toLowerCase())
            : '';

    const isNumber = typeof redux?.tasksMenuSelectedValue === 'number';

    const selectedItem =
        taskMenuItems.length > 0
            ? taskMenuItems.find((mi) =>
                  isNumber
                      ? mi.id === redux.tasksMenuSelectedValue
                      : toCamelCase(redux.tasksMenuSelectedValue?.displayName) === toCamelCase(mi.item?.filterName || '')
              )
            : undefined;

    const defaultItem =
        taskMenuItems.length > 0
            ? taskMenuItems.find(
                  (mi) => mi.item && mi.item.filterName.toLowerCase() === TASK_LIST_MENU_ITEMS.Tasks.value.toLowerCase()
              )
            : undefined;

    // Shift-clicking Add Task opens the legacy full task detail drawer
    const onAddNew = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (e.shiftKey && props.onAddNewTaskLegacy) {
            props.onAddNewTaskLegacy();
        } else {
            props.onAddNewTask();
        }
    };

    const tasksMenuSelectedValue = selectedItem
        ? redux.tasksMenuSelectedValue !== undefined
            ? selectedItem.id
            : ''
        : defaultItem === undefined
        ? ''
        : String(defaultItem.id);

    return (
        <LoadingIndicator isLoading={isLoadingAllLocations}>
            <div className='align-items-center mb-3 task-list-header'>
                <div className='location-selector col-md-5'>
                    <div className='alert alert-light d-flex justify-content-between align-items-center' onClick={onOpenLocationSelector}>
                        {getSelectedLocationsDisplayNames()}
                        <Icon name='caret-down' />
                    </div>
                </div>
                {!props.isMobile ? (
                    <div className='col-md-7 d-flex justify-content-end align-items-center'>
                        {props.bulkUpdateMode && (
                            <Fragment>
                                {!isMultipleLocationsDisabled && (
                                    <button className='btn btn-outline-secondary me-2' onClick={props.bulkUpdateByJobTitle}>
                                        <Icon name='user-tag' /> Assign By Job Title
                                    </button>
                                )}
                                <button className='btn btn-outline-secondary me-2' onClick={props.bulkUpdateResponsibleUser}>
                                    <Icon name='user' /> Change Responsible
                                </button>
                                <button className='btn btn-outline-secondary me-2' onClick={props.bulkUpdateStatus}>
                                    <Icon name='heartbeat' /> Change Status
                                </button>
                                {[CUSTOMER_ROLES.Admin.name, CUSTOMER_ROLES.Owner.name].includes(props.userRole) && (
                                    <button className='btn btn-outline-secondary me-2' onClick={props.bulkUpdateInactive}>
                                        <Icon name='minus-circle' /> Inactive
                                    </button>
                                )}
                                <button className='btn btn-outline-danger' onClick={props.changeBulkUpdateMode}>
                                    <Icon name='dot-circle' /> End Bulk Update
                                </button>
                            </Fragment>
                        )}
                    </div>
                ) : null}
            </div>

            <div className='task-list'>
                {/* Responsive container for tabs and controls */}
                <div className='row g-0'>
                    {/* Tabs section - takes full width on small screens, auto width on larger screens */}
                    <div className='col-12 col-lg-auto flex-lg-grow-1'>
                        <ul className='nav nav-tabs w-100 visual-tabs'>
                            {viewOptions.map((tab) => (
                                <li key={tab.id} className={`nav-item ${selectedIndex === tab.id ? 'active' : ''}`}>
                                    <a
                                        className={`nav-link ${selectedIndex === tab.id ? 'active' : ''}`}
                                        onClick={() => handleTabClick(tab.id)}>
                                        <i className={`fa-regular ${tab.icon} me-2`}></i>
                                        <span>{tab.name}</span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Controls section - stacks below tabs on small screens, inline on larger screens */}
                    <div className='col-12 col-lg-auto'>
                        <div className='d-flex gap-2 align-items-center justify-content-end justify-content-lg-end'>
                            {props.selectedView === VIEWS.List.value && (
                                <MultiSelectBox
                                    className='custom-multiselect'
                                    name='tasksMenu'
                                    idField='id'
                                    valueField='name'
                                    options={getTasksMenuOptions()}
                                    onChange={onTasksMenuChange}
                                    selectedValues={tasksMenuSelectedValue.toString()}
                                    isMulti={false}
                                    isSearchable={false}
                                    optionComponent={TaskListOption}
                                    shouldShowIcons={false}
                                    forceDesktopLayout={true}
                                />
                            )}
                            <button className='btn btn-outline-primary d-flex align-items-center gap-2' onClick={props.onFiltersClick}>
                                <i className='fa-regular fa-bars-filter'></i>
                                <span className='d-none d-sm-inline'>Filters</span>
                            </button>
                            <button
                                className='btn btn-primary d-flex align-items-center gap-2'
                                disabled={isMultipleLocationsDisabled}
                                onClick={onAddNew}>
                                <i className='fa-regular fa-circle-plus'></i>
                                <span className='d-none d-sm-inline'>Add Task</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <LocationSelectorDrawer
                onComplete={onLocationSelectionComplete}
                onCancel={onLocationSelectionCancel}
                isSingleSelect={false}
                isOpen={showLocationSelectorDrawer}
            />
        </LoadingIndicator>
    );
}

export default TaskListHeader;
